/**
 * InstallGateScreen.js
 *
 * 초대 링크로 회원가입 완료 후 표시되는 앱 설치 강제 게이트.
 * - 설치(또는 홈 화면 추가) 전까지 앱 사용 불가
 * - Android: 앱 설치 버튼 (beforeinstallprompt)
 * - iOS Safari: 공유 버튼 → 홈 화면에 추가 단계별 안내
 * - iOS Chrome: ⋯ 메뉴 → 홈 화면에 추가 안내
 * - iOS 인앱 브라우저: Safari/Chrome으로 열기 안내 + 링크 복사
 * - 설치 후 PWA로 열면 초대받은 방으로 자동 입장
 */
import { useState } from 'react';

const isAndroid = () => /Android/.test(navigator.userAgent);
const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent);
const isIOSSafari = () => isIOS() && /Safari\//.test(navigator.userAgent) && !/CriOS\//.test(navigator.userAgent);
const isIOSChrome = () => isIOS() && /CriOS\//.test(navigator.userAgent);
const isInAppBrowser = () => {
  const ua = navigator.userAgent;
  if (/NAVER|KAKAOTALK|Instagram|FBAN|FBAV|Line\/|MicroMessenger/i.test(ua)) return true;
  if (isIOS() && !/Safari\//.test(ua) && !/CriOS\//.test(ua)) return true;
  return false;
};

export default function InstallGateScreen({ installPrompt, onInstallPromptUsed }) {
  const [installing, setInstalling] = useState(false);
  const [copied, setCopied] = useState(false);

  const android = isAndroid();
  const ios = isIOS();
  const iosSafari = isIOSSafari();
  const iosChrome = isIOSChrome();
  const inApp = isInAppBrowser();

  // 데스크탑·태블릿은 게이트 불필요 → null 반환
  if (!android && !ios) return null;

  const handleInstallAndroid = async () => {
    if (!installPrompt) return;
    setInstalling(true);
    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') onInstallPromptUsed?.();
    } catch (e) {
      console.warn('[InstallGate] prompt error:', e);
    }
    setInstalling(false);
  };

  const handleCopyLink = () => {
    const url = window.location.origin + '/auth?mode=login';
    navigator.clipboard.writeText(url).catch(() => {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'linear-gradient(160deg, #eff6ff 0%, #f0fdf4 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px', boxSizing: 'border-box', overflowY: 'auto',
    }}>

      {/* 앱 아이콘 */}
      <img
        src="/heyheylogo.png"
        alt="AI Schedule"
        style={{
          width: 100, height: 100, borderRadius: 24, flexShrink: 0,
          boxShadow: '0 8px 32px rgba(59,130,246,0.35)',
          marginBottom: 20, objectFit: 'cover',
        }}
      />

      {/* 제목 */}
      <div style={{ fontWeight: 800, fontSize: 22, color: '#1e293b', marginBottom: 4 }}>AI Schedule</div>
      <div style={{ fontWeight: 700, fontSize: 17, color: '#3b82f6', marginBottom: 32 }}>가입 완료! 🎉</div>

      {/* ── 안드로이드 ── */}
      {android && (
        <div style={{ width: '100%', maxWidth: 320 }}>
          {installPrompt ? (
            <button
              onClick={handleInstallAndroid}
              disabled={installing}
              style={{
                width: '100%', padding: '17px',
                background: installing ? '#93c5fd' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
                color: '#fff', border: 'none', borderRadius: 14,
                fontSize: 17, fontWeight: 700,
                cursor: installing ? 'not-allowed' : 'pointer',
                boxShadow: '0 6px 20px rgba(59,130,246,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}
            >
              {installing ? '설치 중...' : '설치'}
            </button>
          ) : (
            <div style={{
              background: '#f0f9ff', border: '1px solid #bae6fd',
              borderRadius: 12, padding: '16px', textAlign: 'center',
              fontSize: 14, color: '#0369a1', lineHeight: 1.8,
            }}>
              이미 설치된 경우 홈 화면에서 앱을 열어주세요.<br />
              <span style={{ fontSize: 12, color: '#64748b' }}>
                설치 안 됐다면 Chrome 메뉴(⋮) → 홈 화면에 추가
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── iOS ── */}
      {ios && (
        <div style={{ width: '100%', maxWidth: 320 }}>
          {inApp ? (
            /* 인앱 브라우저: Safari/Chrome으로 열기 유도 */
            <div style={{ textAlign: 'center' }}>
              <div style={{
                background: '#fef3c7', border: '1px solid #fcd34d',
                borderRadius: 12, padding: '16px', marginBottom: 16,
                fontSize: 13, color: '#92400e', lineHeight: 1.8,
              }}>
                <strong>앱 내 브라우저로 열려 있어요.</strong><br />
                Safari 또는 Chrome에서 링크를 열어주세요.
              </div>
              <button
                onClick={handleCopyLink}
                style={{
                  width: '100%', padding: '15px',
                  background: copied ? '#f0fdf4' : '#1e293b',
                  color: copied ? '#16a34a' : '#fff',
                  border: copied ? '1px solid #bbf7d0' : 'none',
                  borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {copied ? '✓ 링크 복사됨!' : '🔗 링크 복사하기'}
              </button>
            </div>
          ) : iosSafari ? (
            /* Safari: 홈 화면 추가 안내 */
            <div style={{
              background: '#f0f9ff', border: '1px solid #bae6fd',
              borderRadius: 16, padding: '20px',
            }}>
              <div style={{
                fontWeight: 700, fontSize: 14, color: '#0369a1',
                marginBottom: 14, textAlign: 'center',
              }}>
                Safari에서 홈 화면 추가하기
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { step: 1, icon: '□↑', text: '하단 공유 버튼 탭' },
                  { step: 2, icon: '＋', text: '"홈 화면에 추가" 선택' },
                  { step: 3, icon: '✓', text: '오른쪽 상단 "추가" 탭' },
                ].map(({ step, icon, text }) => (
                  <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: '#3b82f6', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 700,
                    }}>{step}</div>
                    <div style={{ fontSize: 14, color: '#1e40af', fontWeight: 500 }}>
                      <span style={{ marginRight: 6, fontSize: 16 }}>{icon}</span>{text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : iosChrome ? (
            /* Chrome iOS: 홈 화면 추가 안내 */
            <div style={{
              background: '#f0f9ff', border: '1px solid #bae6fd',
              borderRadius: 16, padding: '20px',
            }}>
              <div style={{
                fontWeight: 700, fontSize: 14, color: '#0369a1',
                marginBottom: 14, textAlign: 'center',
              }}>
                Chrome에서 홈 화면 추가하기
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { step: 1, icon: '⋯', text: '우측 하단 메뉴 탭' },
                  { step: 2, icon: '＋', text: '"홈 화면에 추가" 선택' },
                  { step: 3, icon: '✓', text: '"추가" 탭' },
                ].map(({ step, icon, text }) => (
                  <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: '#3b82f6', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 700,
                    }}>{step}</div>
                    <div style={{ fontSize: 14, color: '#1e40af', fontWeight: 500 }}>
                      <span style={{ marginRight: 6, fontSize: 16 }}>{icon}</span>{text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* 기타 iOS 브라우저 */
            <div style={{
              background: '#f0f9ff', border: '1px solid #bae6fd',
              borderRadius: 14, padding: '18px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 13, color: '#1e40af', lineHeight: 1.8, marginBottom: 12 }}>
                Safari 또는 Chrome에서<br />
                공유 버튼 → <strong>"홈 화면에 추가"</strong>를 선택하세요.
              </div>
              <button
                onClick={handleCopyLink}
                style={{
                  width: '100%', padding: '12px',
                  background: copied ? '#f0fdf4' : '#1e293b',
                  color: copied ? '#16a34a' : '#fff',
                  border: 'none', borderRadius: 10,
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {copied ? '✓ 복사됨!' : '🔗 링크 복사'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* 데스크탑 (예외 케이스) */}
      {!android && !ios && (
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0',
          borderRadius: 14, padding: '20px', maxWidth: 320, textAlign: 'center',
        }}>
          <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.8 }}>
            모바일 기기에서 접속하여<br />앱을 설치해주세요.
          </div>
        </div>
      )}

      {/* 하단 안내 */}
      <div style={{
        marginTop: 28, fontSize: 12, color: '#94a3b8',
        textAlign: 'center', lineHeight: 1.7,
      }}>
        설치 후 앱을 열면 초대받은 방으로<br />자동으로 입장돼요.
      </div>
    </div>
  );
}
